import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Heading, HStack, IconButton, Input, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
  Table, Thead, Tbody, Tr, Th, Td, useDisclosure, useToast, Text,
  FormControl, FormLabel, Switch
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, RepeatIcon, EmailIcon, SearchIcon } from '@chakra-ui/icons';
import Sidebar2 from '../shared/Sidebar2';
import maylawanService from '../services/maylawanService';

const MaylawanAdmin = () => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const createDisc = useDisclosure();
  const editDisc = useDisclosure();
  const emailDisc = useDisclosure();

  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createSendEmail, setCreateSendEmail] = useState(false);

  const [editEmail, setEditEmail] = useState('');
  const [editPassword, setEditPassword] = useState('');

  const [emailSubject, setEmailSubject] = useState('');
  const [emailText, setEmailText] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await maylawanService.list({ search, page, limit });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast({ status: 'error', title: 'فشل الجلب' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [page, limit]);

  const onSearch = async () => { setPage(1); await fetchData(); };

  const onCreate = async () => {
    if (!createEmail || !createPassword) { toast({ status: 'warning', title: 'أدخل البريد وكلمة السر' }); return; }
    try {
      await maylawanService.create({ email: createEmail, password: createPassword, sendWelcomeEmail: createSendEmail });
      toast({ status: 'success', title: 'تم الإنشاء' });
      setCreateEmail(''); setCreatePassword(''); setCreateSendEmail(false);
      createDisc.onClose();
      fetchData();
    } catch (e) { toast({ status: 'error', title: 'فشل الإنشاء' }); }
  };

  const onEditOpen = (row) => { setSelected(row); setEditEmail(row.email); setEditPassword(''); editDisc.onOpen(); };
  const onEditSave = async () => {
    try {
      await maylawanService.update(selected._id, { email: editEmail, password: editPassword || undefined });
      toast({ status: 'success', title: 'تم التحديث' });
      editDisc.onClose();
      fetchData();
    } catch (e) { toast({ status: 'error', title: 'فشل التحديث' }); }
  };

  const onDelete = async (row) => {
    if (!window.confirm('حذف هذا المستخدم؟')) return;
    try { await maylawanService.remove(row._id); toast({ status: 'success', title: 'تم الحذف' }); fetchData(); }
    catch { toast({ status: 'error', title: 'فشل الحذف' }); }
  };

  const onResetDevice = async (row) => {
    try { await maylawanService.resetDevice(row._id); toast({ status: 'success', title: 'تمت إعادة تعيين الجهاز' }); fetchData(); }
    catch { toast({ status: 'error', title: 'فشل العملية' }); }
  };

  const onSendEmailOpen = (row) => { setSelected(row); setEmailSubject(''); setEmailText(''); emailDisc.onOpen(); };
  const onSendEmail = async () => {
    try { await maylawanService.sendEmail(selected._id, { subject: emailSubject, text: emailText }); toast({ status: 'success', title: 'تم الإرسال' }); emailDisc.onClose(); }
    catch { toast({ status: 'error', title: 'فشل الإرسال' }); }
  };

  return (
    <Box width="100%" dir="rtl">
      <Sidebar2 />
      <Box p={6} pr={{ base: 0, md: '70px' }}>
        <HStack justify="space-between" mb={4}>
          <Heading size="md">إدارة مستخدمي Maylawan</Heading>
          <Button leftIcon={<AddIcon />} colorScheme="yellow" onClick={createDisc.onOpen}>مستخدم جديد</Button>
        </HStack>

        <HStack mb={4}>
          <Input placeholder="بحث بالبريد" value={search} onChange={(e) => setSearch(e.target.value)} />
          <IconButton aria-label="بحث" icon={<SearchIcon />} onClick={onSearch} isLoading={loading} />
        </HStack>

        <Table variant="striped" size="sm">
          <Thead>
            <Tr>
              <Th>البريد</Th>
              <Th>الجهاز</Th>
              <Th>تم الإنشاء</Th>
              <Th>إجراءات</Th>
            </Tr>
          </Thead>
          <Tbody>
            {items.map((row) => (
              <Tr key={row._id}>
                <Td>{row.email}</Td>
                <Td>{row.deviceId || '-'}</Td>
                <Td>{new Date(row.createdAt).toLocaleString()}</Td>
                <Td>
                  <HStack>
                    <IconButton aria-label="تعديل" icon={<EditIcon />} size="sm" onClick={() => onEditOpen(row)} />
                    <IconButton aria-label="حذف" icon={<DeleteIcon />} size="sm" onClick={() => onDelete(row)} />
                    <IconButton aria-label="إعادة الجهاز" icon={<RepeatIcon />} size="sm" onClick={() => onResetDevice(row)} />
                    <IconButton aria-label="إرسال بريد" icon={<EmailIcon />} size="sm" onClick={() => onSendEmailOpen(row)} />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {/* Create Modal */}
        <Modal isOpen={createDisc.isOpen} onClose={createDisc.onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>إنشاء مستخدم Maylawan</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={3}>
                <FormLabel>البريد</FormLabel>
                <Input value={createEmail} onChange={(e) => setCreateEmail(e.target.value)} />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>كلمة السر</FormLabel>
                <Input type="password" value={createPassword} onChange={(e) => setCreatePassword(e.target.value)} />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">إرسال بريد ترحيبي</FormLabel>
                <Switch isChecked={createSendEmail} onChange={(e) => setCreateSendEmail(e.target.checked)} />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="yellow" mr={3} onClick={onCreate}>إنشاء</Button>
              <Button onClick={createDisc.onClose}>إلغاء</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Edit Modal */}
        <Modal isOpen={editDisc.isOpen} onClose={editDisc.onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>تعديل مستخدم</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={3}>
                <FormLabel>البريد</FormLabel>
                <Input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
              </FormControl>
              <FormControl mb={3}>
                <FormLabel>كلمة سر جديدة (اختياري)</FormLabel>
                <Input type="password" value={editPassword} onChange={(e) => setEditPassword(e.target.value)} />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="yellow" mr={3} onClick={onEditSave}>حفظ</Button>
              <Button onClick={editDisc.onClose}>إلغاء</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Send Email Modal */}
        <Modal isOpen={emailDisc.isOpen} onClose={emailDisc.onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>إرسال بريد</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <FormControl mb={3}>
                <FormLabel>الموضوع</FormLabel>
                <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>النص</FormLabel>
                <Input value={emailText} onChange={(e) => setEmailText(e.target.value)} />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="yellow" mr={3} onClick={onSendEmail}>إرسال</Button>
              <Button onClick={emailDisc.onClose}>إلغاء</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Box>
  );
};

export default MaylawanAdmin;


